import React, {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactSelect, {
  components,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  OptionProps,
  GroupBase,
  Props,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  ControlProps,
  MenuListProps,
  InputProps,
  ValueContainerProps,
  OnChangeValue,
  MultiValue,
  MenuProps,
  MultiValueProps,
  InputActionMeta,
  SelectInstance,
  PlaceholderProps,
} from 'react-select';
import { Capacitor } from '@capacitor/core';
import c from 'classnames';
import { debounce, kebabCase } from 'lodash';
import CreatableSelect from 'react-select/creatable';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { ModalsZIndex } from 'src/components/constants';
import { FlexColumn } from 'src/components/domain/contacts/contact-details/shared';
import { ButtonV2, StyledButtonV2 } from 'src/components/general/button/lgg-button';
import {
  Chip,
  TextChip,
  ImageChip,
  BaseChipProps,
} from 'src/components/general/display/chip';
import { useBottomDrawerHeight } from 'src/components/general/drawer/bottom/bottom-drawer';
import { LggDrawer } from 'src/components/general/drawer/lgg-drawer';
import { Icon } from 'src/components/general/icon';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { LoadingSpinner } from 'src/components/general/inputs/shared';
import { Expand } from 'src/components/layout/expand';
import { FlexRow } from 'src/components/layout/flex-row';
import { useOverlayStackDepth } from 'src/components/providers/overlay-stack-depth-provider';
import { SafeAreaBottom } from 'src/components/providers/safe-area-insets-provider';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useGetVisualViewport } from 'src/hooks/use-get-visual-viewport';
import { useVisible } from 'src/hooks/use-visible';
import {
  inputBorder,
  inputFocusBorder,
  labelTextStyle,
} from 'src/theme/sub-themes/inputs-theme';
export type SelectOption<T = string | number> = {
  label: string;
  subTitle?: ReactNode;
  value: T;
  isDisabled?: boolean;
  // In a multi select, hides the option's remove icon. Preventing its removal through
  // other means (backspace, clearing, deselecting in the menu) is up to the onChange.
  isFixed?: boolean;
  chip?: TextChip | ImageChip;
  icon?: string | React.ReactNode;
  isSelected?: boolean;
};

export type GroupedSelectOption<T = string | number> = {
  label: string;
  options: SelectOption<T>[];
};

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectInnerWrapper = styled.div`
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;

const selectTextStyles = css`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 17px;
  text-align: left;
`;

const TriggerOverrideContainer = styled.div`
  width: max-content;

  .lgg-select__control {
    padding: 0 !important;
    width: max-content !important;
    height: max-content !important;
    min-height: max-content !important;
    border: none !important;
    box-shadow: none !important;
  }

  .lgg-select__value-container {
    .lgg-select__multi-value {
      display: none;
    }
  }

  .lgg-select__value-container--is-multi {
    &.lgg-select__value-container--has-value {
      margin: 0 !important;
    }
  }
`;

const selectStyles = css`
  width: 100%;

  // Container

  .lgg-select__control {
    ${inputBorder};
    cursor: pointer;
    min-height: 38px;
    padding: 8px 10px 8px 10px;
    width: 100%;

    &:hover {
      ${inputBorder};
    }

    // Container disable state

    &.lgg-select__control--is-disabled {
      background-color: ${({ theme }) => theme.colors.porcelain};
    }

    // Container focused state

    &.lgg-select__control--is-focused {
      ${inputFocusBorder};
    }

    // Container menu open state

    &.lgg-select__control--menu-is-open {
      // Arrow icon

      .lgg-select__indicator .lgg-icon.arrow-icon {
        transform: rotate(180deg);
      }
    }

    ${up('md')} {
      padding-right: 0;
    }
  }

  &.input-error {
    .lgg-select__control {
      ${({ theme }) => theme.inputsTheme.errorBorder};
    }
  }

  // Container drawer

  &.lgg-drawer-select {
    .control-container:not(.is-searchable) {
      display: none;
    }

    .control-container {
      border-bottom: solid 1px ${({ theme }) => theme.colors.koala};
      padding: 10px 15px;
    }
  }

  // Styles normalizer

  .lgg-select__control .lgg-select__value-container,
  .lgg-select__group {
    padding: 0;
  }

  .lgg-select__control .lgg-select__value-container {
    min-height: 15px;
  }

  .lgg-select__control .lgg-select__value-container > input {
    height: 0;
  }

  // Placeholder

  .lgg-select__placeholder {
    color: ${({ theme }) => theme.colors.geyser};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 13px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    letter-spacing: normal;
    line-height: 17px;
    margin: 0;
    text-wrap: nowrap;
  }

  &.lgg-drawer-select {
    .lgg-select__placeholder {
      margin-left: 26px;
    }
  }

  // Selected value when !isMulti

  .lgg-select__control {
    .lgg-select__single-value {
      margin-left: 0;
      ${selectTextStyles};
    }

    &:not(.lgg-select__control--menu-is-open) {
      .lgg-select__input-container {
        position: absolute;
        z-index: -1;
      }
    }
  }

  .control-container.is-searchable {
    .lgg-select__control.lgg-select__control--menu-is-open {
      // Selected value when !isMulti

      .lgg-select__single-value {
        ${selectTextStyles};
        color: ${({ theme }) => theme.colors.geyser};
      }
    }
  }

  // Selected values when isMulti (tags)

  .lgg-select__multi-value {
    background-color: ${({ theme }) => theme.colors.porcelain};
    border-radius: 11px;
    padding: 5px 0 4px 6px;
    // Text

    .lgg-select__multi-value__label {
      align-items: center;
      display: flex;
      flex-direction: row;
      padding: 0;
    }

    // Clear tag icon

    .lgg-select__multi-value__remove {
      cursor: pointer;
      padding: 0 6px;

      &:hover {
        background-color: unset;
      }
    }
  }

  &.is-tag-select .lgg-select__multi-value {
    background-color: ${({ theme }) => theme.colors.secondaryMint20};
  }

  &:not(.lgg-drawer-select) {
    .lgg-select__value-container--is-multi.lgg-select__value-container--has-value {
      margin: -5px;
    }
  }

  // Search input styles

  .lgg-select__value-container {
    display: flex;

    .lgg-select__input {
      ${up('md')} {
        height: 0;
      }
    }
  }

  .lgg-select__input-container {
    ${selectTextStyles};
    margin: 0;
    padding: 0;
  }

  &.lgg-drawer-select {
    .lgg-select__input-container {
      margin-left: 26px;
      position: absolute;
    }
  }

  // Indicators container (arrow, clear, search, loading)

  .lgg-select__indicators {
    align-items: center;
    align-self: center;
    display: flex;
    height: 100%;
    padding: 0 15px 0 10px;

    .lgg-select__indicator {
      padding: 0;
    }

    .lgg-icon.arrow-icon {
      transition: transform 150ms;

      path {
        fill: ${({ theme }) => theme.colors.casper};
      }
    }
  }

  // Options overlay menu

  .lgg-select__menu {
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin-top: 0;
    min-width: 226px;

    ${up('md')} {
      border-radius: 6px;
      border: solid 1px ${({ theme }) => theme.colors.koala};
      box-shadow: 0 20px 40px 0 #5b65701a;
      margin-top: 3px;
    }
  }

  .lgg-select__menu-list {
    padding: 15px;

    ${up('md')} {
      padding: 5px;
    }
  }

  &.lgg-drawer-select.is-multi-select {
    &.is-showing-selected-count,
    &.is-multi-grouped-select:not(.is-showing-selected-count) {
      .lgg-select__menu-list {
        padding-top: 0;
      }
    }
  }

  &.lgg-drawer-select.is-grouped-select {
    .lgg-select__menu-list {
      padding-top: 0;
    }
  }

  // Options

  .lgg-select__option {
    background-color: ${({ theme }) => theme.colors.porcelain};
    border-radius: 6px;
    padding: 0;

    &:active:not(.lgg-select__option--is-selected) {
      background-color: ${({ theme }) => theme.colors.porcelain};
    }

    // Focus options

    &.lgg-select__option--is-focused:not(.lgg-select__option--is-disabled):not(
        .lgg-select__option--is-selected
      ) {
      background-color: ${({ theme }) => theme.colors.porcelain};
    }

    // Loading item

    &.loading-item,
    &.lgg-select__option--is-focused.loading-item:not(
        .lgg-select__option--is-disabled
      ):not(.lgg-select__option--is-selected) {
      background-color: ${({ theme }) => theme.colors.white};
    }

    // Selected option

    &.lgg-select__option--is-selected {
      background-color: ${({ theme }) => theme.colors.secondaryTopaz10};
    }

    ${up('md')} {
      background-color: ${({ theme }) => theme.colors.white};
      border-radius: 4px;

      &:active:not(.lgg-select__option--is-selected) {
        background-color: ${({ theme }) => theme.colors.white};
      }
    }
  }

  .lgg-select__option + .lgg-select__option {
    margin-top: 8px;

    ${up('md')} {
      margin-top: 3px;
    }
  }

  // Group header

  .lgg-select__group-heading {
    border-bottom: solid 1px ${({ theme }) => theme.colors.porcelain};
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 12px;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: -0.12px;
    line-height: 14px;
    margin: 3px 0;
    padding: 12px 10px;
    text-align: left;
    text-transform: unset;

    &:empty {
      display: none;
    }
  }

  &.lgg-drawer-select {
    .lgg-select__group-heading {
      border-bottom: none;
      padding-left: 0;
      text-transform: uppercase;
    }
  }
`;

const StyledSelect = styled(ReactSelect)`
  ${selectStyles};
` as typeof ReactSelect;

const StyledCreatableSelect = styled(CreatableSelect)`
  ${selectStyles};
` as typeof CreatableSelect;

const InvisibleElement = styled.div`
  display: none;
`;

const SearchIcon = styled(Icon)`
  svg {
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.colors.casper};
    }
  }

  .lgg-drawer-select & {
    svg {
      height: 16px;
      width: 16px;
    }
  }
`;

const DropdownIndicator = <
  SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<SelectOption> = GroupBase<SelectOption>,
>(
  props: DropdownIndicatorProps<SelectOption, IsMulti, Group> & { forceCaret?: boolean },
) => {
  const {
    selectProps: { isLoading, isDisabled, isSearchable },
    isFocused,
    hasValue,
    forceCaret = false,
  } = props;

  const renderTrailingIcon = useCallback(() => {
    const arrowIcon = () => <Icon className="arrow-icon" type="arrowdown" />;

    if (forceCaret || isDisabled) {
      return arrowIcon();
    }

    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (isSearchable && isFocused && !hasValue) {
      return <SearchIcon type="magnifyingGlass" />;
    }

    if (!hasValue) {
      return arrowIcon();
    }

    return <InvisibleElement />;
  }, [forceCaret, hasValue, isDisabled, isFocused, isLoading, isSearchable]);

  return (
    <components.DropdownIndicator {...props}>
      {renderTrailingIcon()}
    </components.DropdownIndicator>
  );
};

// These components return `null` to override react-select defaults
const IndicatorSeparator = () => {
  return null;
};

const LoadingIndicator = () => {
  return null;
};

const ClearIcon = styled(Icon)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.koala};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 14px;
  justify-content: center;
  width: 14px;

  svg {
    height: 6px;
    width: 6px;

    path {
      fill: ${({ theme }) => theme.colors.steel};
    }
  }
`;

const ClearIndicator = <
  SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<SelectOption> = GroupBase<SelectOption>,
>(
  props: ClearIndicatorProps<SelectOption, IsMulti, Group>,
) => {
  const {
    innerProps: { ref, ...restInnerProps },
    selectProps: { isLoading },
  } = props;

  if (isLoading) {
    return <InvisibleElement />;
  }

  return (
    <div {...restInnerProps} ref={ref} data-lgg-id="clear-icon">
      <ClearIcon type="close" />
    </div>
  );
};

const StyledOption = styled(FlexRow)`
  align-items: center;
  padding: 7px 15px 7px 10px;
  min-height: 40px;

  ${up('md')} {
    min-height: 32px;
    padding: 0 10px;
  }
`;

const OptionLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  font-stretch: normal;
  font-style: normal;
  font-weight: 400;
  letter-spacing: normal;
  line-height: 19px;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;

  .lgg-select__option--is-selected & {
    color: ${({ theme }) => theme.colors.gogo};
  }

  .lgg-select__option--is-disabled & {
    color: ${({ theme }) => theme.colors.flint};
    cursor: not-allowed;
    opacity: 0.5;
  }

  .lgg-select__option--is-focused:not(.lgg-select__option--is-selected) & {
    color: ${({ theme }) => theme.colors.smalt};
  }

  ${up('md')} {
    font-size: 12px;
    line-height: 15px;
  }
`;

const SelectedItemIcon = styled(Icon)`
  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }

    ${up('md')} {
      height: 12px;
      width: 12px;
    }
  }
`;

const StyledLoadingItem = styled(FlexRow)`
  align-items: center;
  height: 40px;
  justify-content: center;

  ${up('md')} {
    height: 32px;
  }
`;

const OptionIcon = styled(Icon)`
  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.smalt};
    }
  }
`;

const OptionSubTitle = styled.span`
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  color: ${({ theme }) => theme.colors.flint};
  min-width: 0;
  width: 100%;
`;

const LoadingItem = () => {
  return (
    <StyledLoadingItem
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <LoadingSpinner size="20px" />
    </StyledLoadingItem>
  );
};

const OptionContentColumn = styled(FlexColumn)`
  justify-content: center;
  width: 96%;
`;

const OptionComponentChip = styled(Chip)`
  margin: 0;
`;

const OptionComponentContent = styled(FlexRow)`
  width: 100%;
  gap: 10px;
`;

const OptionComponent = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group> & {
    chipSize: BaseChipProps['size'];
    renderOption?: (option: Option) => React.ReactNode;
  },
) => {
  const {
    data: { label, value, subTitle, chip, icon },
    isSelected,
    selectProps: { name },
    chipSize,
    renderOption,
  } = props;

  const parsedValue = `${value.toString().replaceAll(':', '_')}`;
  const isLoadingItem = value === LOADING_ITEM.value;

  return (
    <components.Option
      {...props}
      className={c({
        'loading-item': isLoadingItem,
      })}
    >
      {isLoadingItem ? (
        <LoadingItem />
      ) : (
        <StyledOption data-lgg-id={`select-option-${name}-${parsedValue}`}>
          {renderOption ? (
            renderOption({ ...props.data, isSelected })
          ) : (
            <OptionComponentContent>
              {chip && <OptionComponentChip size={chipSize} {...chip} />}
              {icon && typeof icon === 'string' ? <OptionIcon type={icon} /> : icon}
              <OptionContentColumn>
                <OptionLabel>{label}</OptionLabel>
                {subTitle ? <OptionSubTitle>{subTitle}</OptionSubTitle> : null}
              </OptionContentColumn>
            </OptionComponentContent>
          )}
          <Expand style={{ minWidth: '28px' }} />
          {isSelected && <SelectedItemIcon type="checkSave" />}
        </StyledOption>
      )}
    </components.Option>
  );
};

const TagLabel = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  line-height: 13px;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  align-items: center;

  .is-tag-select & {
    color: ${({ theme }) => theme.colors.secondaryMintDark};
  }
`;

const MultiValueLabel = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueGenericProps<Option, IsMulti, Group>,
) => {
  const {
    data,
    selectProps: { name },
  } = props;
  const { showMultiValueIcon } = useContext(SelectContext);

  return (
    <components.MultiValueLabel {...props}>
      <FlexRow
        style={{ overflow: 'hidden' }}
        data-lgg-id={`${name}-${kebabCase(data.label)}`}
      >
        {data.chip && <Chip size="small" {...data.chip} />}
        {showMultiValueIcon && data.icon ? (
          typeof data.icon === 'string' ? (
            <OptionIcon type={data.icon} />
          ) : (
            data.icon
          )
        ) : null}
        <TagLabel>{props.children}</TagLabel>
      </FlexRow>
    </components.MultiValueLabel>
  );
};

const RemoveTagIcon = styled(Icon)`
  svg {
    height: 8px;
    width: 8px;

    path {
      fill: ${({ theme }) => theme.colors.casper};
    }
  }
`;

const MultiValueRemove = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => {
  const {
    selectProps: { name },
    data: { label, isFixed },
  } = props;

  if (isFixed) {
    return null;
  }

  return (
    <components.MultiValueRemove {...props}>
      <RemoveTagIcon type="close" lggTestId={`${name}-close-icon-${kebabCase(label)}`} />
    </components.MultiValueRemove>
  );
};

const StyledNoOptionsFound = styled.div`
  color: ${({ theme }) => theme.colors.geyser};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 14px;
  text-align: left;
`;

export const NoOptionsFound = (props) => {
  const { t } = useTranslation(['common']);

  return <StyledNoOptionsFound {...props}>{t('common:noOptions')}</StyledNoOptionsFound>;
};

const Label = styled.span`
  ${labelTextStyle};
  margin-bottom: 1px;
`;

const ControlTriggerOverrideContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TriggerOverrideHiddenContent = styled.div`
  visibility: hidden;
  height: 0;
  width: 0;
`;

const Control = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ControlProps<Option, IsMulti, Group>,
) => {
  const {
    children,
    selectProps: { isSearchable, name, className },
  } = props;

  const { triggerOverride, triggerTargetOverrideRef } = useContext(SelectContext);
  const hasError = className?.includes('input-error');

  if (triggerOverride) {
    return (
      <TriggerOverrideContainer>
        <components.Control {...props}>
          <ControlTriggerOverrideContainer ref={triggerTargetOverrideRef}>
            {triggerOverride}
            <TriggerOverrideHiddenContent>{children}</TriggerOverrideHiddenContent>
          </ControlTriggerOverrideContainer>
        </components.Control>
      </TriggerOverrideContainer>
    );
  }

  return (
    <div
      className={c({
        'control-container': true,
        'is-searchable': isSearchable,
        error: hasError,
      })}
      data-lgg-id={name}
    >
      <components.Control {...props}>{children}</components.Control>
    </div>
  );
};

export function multiValueAsValue<Option, IsMulti extends boolean>(
  multiValue: MultiValue<Option>,
): OnChangeValue<Option, IsMulti> {
  return multiValue as OnChangeValue<Option, IsMulti>;
}

const SelectedCountLabel = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  line-height: 14px;
  padding-top: 12px;
  text-align: left;
`;

const SelectedChip = styled(FlexRow)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.secondaryTopaz10};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  height: 40px;
  letter-spacing: normal;
  line-height: 18px;
  padding: 11px 15px;
  text-align: left;
  white-space: nowrap;

  .is-tag-select & {
    background-color: ${({ theme }) => theme.colors.secondaryMint20};
    color: ${({ theme }) => theme.colors.secondaryMintDark};
  }

  svg {
    height: 14px;
    margin-left: 10px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.colors.geyser};
    }
  }

  & + & {
    margin-left: 8px;
  }
`;

const SelectionContainer = styled(FlexColumn)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  margin-bottom: 15px;
  margin-left: -15px;
  margin-right: -15px;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 15px 15px 15px;

  .is-multi-grouped-select & {
    margin-bottom: 0;
  }
`;

const buttonsBarHeight = 51;

const SearchInputPlaceholder = styled.span`
  color: ${({ theme }) => theme.colors.geyser};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
  padding-top: 9px;
  position: absolute;
  top: 0;
`;

const DropdownSearchContainer = styled(FlexRow)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  min-height: 32px;
  overflow: hidden;
  padding: 9px 15px 9px 10px;
  position: relative;

  .lgg-select__input-container {
    display: flex;
    width: 100%;

    .lgg-select__input {
      color: ${({ theme }) => theme.colors.smalt} !important;
      font-family: ${({ theme }) => theme.font.regular} !important;
      font-size: 12px !important;
      line-height: 15px !important;

      ::placeholder {
        color: ${({ theme }) => theme.colors.geyser} !important;
      }
    }

    .lgg-select__input-placeholder {
      color: ${({ theme }) => theme.colors.geyser};
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 12px;
      line-height: 15px;
      position: absolute;
      top: 0;
    }

    &:after {
      display: none;
    }
  }
`;

const SearchPortalContainer = styled.div`
  cursor: text;
  overflow: hidden;
  width: 100%;
`;

const useScrollBottomLoadMore = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = useRef(false);
  const { onLoadMore } = useContext(SelectContext);

  useEffect(() => {
    const menu = containerRef.current;

    if (menu && onLoadMore && !isLoadingMoreRef.current) {
      const onScroll = async () => {
        if (menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 50) {
          isLoadingMoreRef.current = true;
          await onLoadMore();
          isLoadingMoreRef.current = false;
        }
      };

      menu.addEventListener('scroll', onScroll);

      return () => {
        menu?.removeEventListener('scroll', onScroll);
      };
    }
  }, [onLoadMore]);

  return {
    containerRef,
  };
};

const DesktopMenuList = <
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  const { ...rest } = props;
  const {
    isSearching,
    showDesktopSearch,
    searchPortalRef,
    onSearchPortalReady,
    searchText,
    inputName,
    searchPlaceholder,
  } = useContext(SelectContext);
  const { containerRef } = useScrollBottomLoadMore();
  const { t } = useTranslation(['common']);

  useEffect(() => {
    onSearchPortalReady();
  }, [onSearchPortalReady]);

  return (
    <>
      {showDesktopSearch && (
        <DropdownSearchContainer data-lgg-id="dropdown-search-container">
          <SearchPortalContainer
            ref={searchPortalRef}
            data-lgg-id={`${inputName}-search-input-container`}
          >
            {!searchText?.length && (
              <SearchInputPlaceholder>
                {searchPlaceholder || t('common:search')}
              </SearchInputPlaceholder>
            )}
          </SearchPortalContainer>
        </DropdownSearchContainer>
      )}
      {isSearching ? (
        <LoadingItem />
      ) : (
        <components.MenuList
          {...rest}
          innerRef={(ref) => (containerRef.current = ref)}
          maxHeight={props.maxHeight - buttonsBarHeight}
        >
          {props.children}
        </components.MenuList>
      )}
    </>
  );
};

const MenuList = <
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  const { t } = useTranslation(['common']);
  const {
    selectProps: { value, inputValue },
    setValue,
    isMulti,
  } = props;

  const { containerRef } = useScrollBottomLoadMore();
  const { isSearching } = useContext(SelectContext);

  if (isSearching) {
    return <LoadingItem />;
  }

  return (
    <components.MenuList {...props} innerRef={(ref) => (containerRef.current = ref)}>
      {!inputValue && isMulti && value && 'length' in value && value.length > 0 && (
        <>
          <SelectedCountLabel>{`${value.length} ${t(
            'common:selected',
          ).toUpperCase()}`}</SelectedCountLabel>
          <SelectionContainer>
            <FlexRow>
              {value.map((option) => (
                <SelectedChip
                  key={option.value}
                  onClick={(e) => {
                    preventDefaultAndStopPropagation(e);

                    if (value) {
                      setValue(
                        multiValueAsValue(value.filter((s) => s.value !== option.value)),
                        'deselect-option',
                        option,
                      );
                    }
                  }}
                >
                  {option.chip && <Chip size="large" {...option.chip} />}
                  {option.label}
                  <Icon type="close" />
                </SelectedChip>
              ))}
            </FlexRow>
          </SelectionContainer>
        </>
      )}
      {props.children}
    </components.MenuList>
  );
};

const ButtonsContainer = styled(FlexRow)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  height: ${buttonsBarHeight}px;
  padding: 10px;
  position: relative;
  width: 100%;

  ${StyledButtonV2} {
    flex: 1;
    height: 30px;
  }

  ${StyledButtonV2} + ${StyledButtonV2} {
    margin-left: 10px;
  }
`;

const Menu = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuProps<Option, IsMulti, Group>,
) => {
  const { t } = useTranslation();
  const { clearValue, isMulti } = props;

  return (
    <components.Menu {...props}>
      <span data-lgg-id="select-menu-list">
        {props.children}
        {isMulti && (
          <ButtonsContainer>
            <ButtonV2
              variant="default"
              data-lgg-id="select-clear-button"
              type="button"
              size="small"
              onClick={clearValue}
            >
              {t('common:clear')}
            </ButtonV2>
            <ButtonV2
              variant="primary"
              data-lgg-id="select-apply-button"
              size="small"
              type="button"
              onClick={() => {
                const activeElement = document.activeElement;

                if (activeElement) {
                  (activeElement as HTMLElement).blur();
                }
              }}
            >
              {t('common:done')}
            </ButtonV2>
          </ButtonsContainer>
        )}
      </span>
    </components.Menu>
  );
};

const Input = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: InputProps<Option, IsMulti, Group>,
) => {
  return <components.Input {...props}>{props.children}</components.Input>;
};

const DesktopInput = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: InputProps<Option, IsMulti, Group>,
) => {
  const { searchPortalRef, searchPortalReady } = useContext(SelectContext);

  if (!searchPortalReady || !searchPortalRef?.current) {
    return <components.Input {...props} />;
  }

  return createPortal(<components.Input {...props} autoFocus />, searchPortalRef.current);
};

const DesktopValueContainer = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: ValueContainerProps<Option, IsMulti, Group>) => {
  const {
    hasValue,
    isMulti,
    getValue,
    selectProps: { placeholder, inputValue },
  } = props;
  const { renderValue, renderPlaceholder } = useContext(SelectContext);
  const selectedOption = getValue()[0];

  // react-select hides the selected value as soon as there's a search keyword
  // (`renderPlaceholderOrValue` returns null when `inputValue` is set for single
  // selects), and there's no prop to opt out of it. Since our desktop search input
  // is portaled into the menu, the control ends up looking empty while searching,
  // so we render the value ourselves in that case.
  const shouldRenderValue =
    Boolean(inputValue) && hasValue && !isMulti && Boolean(selectedOption);

  return (
    <components.ValueContainer {...props}>
      {!hasValue && (
        <div className="lgg-select__placeholder">
          {renderPlaceholder ? renderPlaceholder() : placeholder}
        </div>
      )}
      {shouldRenderValue && selectedOption && (
        <div className="lgg-select__single-value">
          {renderValue ? (
            renderValue(selectedOption)
          ) : (
            <>
              {selectedOption.icon && typeof selectedOption.icon === 'string' ? (
                <OptionIcon type={selectedOption.icon} />
              ) : (
                selectedOption.icon
              )}
              <span className="lgg-select__single-value-text">
                {selectedOption.label}
              </span>
            </>
          )}
        </div>
      )}
      {children}
    </components.ValueContainer>
  );
};

const ValueContainer = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: ValueContainerProps<Option, IsMulti, Group>) => (
  <components.ValueContainer {...props}>
    <SearchIcon type="magnifyingGlass" style={{ position: 'absolute' }} />
    {children}
  </components.ValueContainer>
);

const StyledMoreItemsSelectedCountChip = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.porcelain};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.flint};
  display: flex;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  font-stretch: normal;
  font-style: normal;
  height: 22px;
  justify-content: center;
  letter-spacing: normal;
  line-height: 13px;
  padding: 6px;
  text-align: left;

  .is-tag-select & {
    background-color: ${({ theme }) => theme.colors.secondaryMint20};
    color: ${({ theme }) => theme.colors.secondaryMintDark};
  }
`;

type MoreItemsSelectedCountChipProps = {
  count: number;
};

const MoreItemsSelectedCountChip = memo<MoreItemsSelectedCountChipProps>(({ count }) => {
  return <StyledMoreItemsSelectedCountChip>+{count}</StyledMoreItemsSelectedCountChip>;
});

const MultiValueComponent = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueProps<Option, IsMulti, Group> & { maxToShow?: number },
) => {
  const { getValue, index, maxToShow = 5 } = props;
  const maxItemsCount = maxToShow;
  const overflow = getValue().slice(maxItemsCount);

  return index < maxItemsCount ? (
    <components.MultiValue {...props}>{props.children}</components.MultiValue>
  ) : index === maxItemsCount ? (
    <MoreItemsSelectedCountChip count={overflow.length} />
  ) : null;
};

const SelectContainer = styled(FlexColumn)`
  width: 100%;

  &.has-custom-trigger {
    width: max-content;
  }
`;

const DrawerHeader = styled(FlexRow)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.porcelain};
  height: 70px;
  margin-bottom: 0;
  padding: 0 20px;
`;

const DrawerTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  flex: 1;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 20px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: -0.4px;
  line-height: 24px;
  margin-right: 20px;
  text-align: left;
`;

const DrawerCloseIcon = styled(Icon)`
  svg {
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const StyledDrawer = styled(LggDrawer).withConfig<{
  menuHeight: number;
}>({
  shouldForwardProp: (prop) => prop !== 'menuHeight',
})`
  .ant-drawer-header {
    border: none;
    padding: 0;
  }

  .ant-drawer-content {
    border-radius: 8px 8px 0 0;
  }

  .ant-drawer-body {
    overflow: hidden;
    padding: 0;
  }

  .ant-drawer-footer {
    padding: 0;
  }

  .lgg-select__menu-list {
    height: ${({ menuHeight }) => menuHeight + 'px'};
    max-height: unset;
  }
`;

const DrawerFooter = styled(FlexRow)`
  align-items: center;
  padding: 15px 20px;

  ${StyledButtonV2} {
    flex: 1;
  }

  ${StyledButtonV2} + ${StyledButtonV2} {
    margin-left: 20px;
  }
`;

const SingleValueContainer = styled(FlexRow)`
  gap: 5px;
`;

export type SelectProps<
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<Props<Option, IsMulti, Group>, 'onChange'>;

type ExtraProps<Option extends SelectOption, IsMulti extends boolean> = {
  label?: string;
  mobileLabel?: string;
  isCreatable?: boolean;
  className?: string;
  onChange: ValueChanged<OnChangeValue<Option, IsMulti>>;
  isTag?: boolean;
  forceCaret?: boolean;
  onLoadMore?: () => Promise<void>;
  onSearch?: (keyword: string) => Promise<void>;
  // Not debounced, use it to derive options from the typed text on every keystroke
  onSearchTextChange?: (keyword: string) => void;
  canLoadMore?: boolean;
  hasError?: boolean;
  triggerOverride?: (value: OnChangeValue<Option, IsMulti>) => React.ReactNode;
  onMenuClose?: VoidFunction;
  hideIndicators?: boolean;
  disableInternalSearch?: boolean;
  renderOption?: (option: Option) => React.ReactNode;
  searchPlaceholder?: string;
  renderValue?: (value: Option) => React.ReactNode;
  renderPlaceholder?: () => React.ReactNode;
  renderOptionIcon?: (option: Option) => React.ReactNode;
  multiValueMaxToShow?: number;
  showMultiValueIcon?: boolean;
};

const SelectContext = React.createContext<{
  onLoadMore?: () => Promise<void>;
  isSearching: boolean;
  setIsLoadingMore: (loading: boolean) => void;
  isLoadingMore: boolean;
  showDesktopSearch: boolean;
  searchPortalRef: React.RefObject<HTMLDivElement> | null;
  searchPortalReady: boolean;
  onSearchPortalReady: () => void;
  searchText?: string;
  inputName?: string;
  triggerOverride?: React.ReactNode;
  triggerTargetOverrideRef: React.RefObject<HTMLDivElement> | null;
  searchPlaceholder?: string;
  renderValue?: (value: SelectOption) => React.ReactNode;
  renderPlaceholder?: () => React.ReactNode;
  renderOptionIcon?: (option: SelectOption) => React.ReactNode;
  showMultiValueIcon?: boolean;
}>({
  onLoadMore: async () => {},
  isSearching: false,
  setIsLoadingMore: () => {},
  isLoadingMore: false,
  showDesktopSearch: false,
  searchPortalRef: null,
  searchPortalReady: false,
  onSearchPortalReady: () => {},
  searchText: undefined,
  inputName: undefined,
  triggerOverride: undefined,
  triggerTargetOverrideRef: null,
  searchPlaceholder: undefined,
  renderValue: undefined,
  renderPlaceholder: undefined,
  renderOptionIcon: () => null,
  showMultiValueIcon: false,
});

const LOADING_ITEM = { value: 'loading_item' };

export const Select = <
  Option extends SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  options,
  value,
  isMulti,
  placeholder,
  onChange,
  isLoading,
  isDisabled,
  menuIsOpen,
  label,
  mobileLabel,
  isSearchable = true,
  isCreatable,
  isClearable = true,
  className = '',
  name,
  isTag,
  forceCaret = false,
  onLoadMore,
  onSearch,
  onSearchTextChange,
  canLoadMore,
  hasError,
  triggerOverride,
  onMenuClose,
  hideIndicators = false,
  disableInternalSearch,
  renderOption,
  searchPlaceholder,
  renderValue,
  renderPlaceholder,
  renderOptionIcon,
  multiValueMaxToShow = 5,
  showMultiValueIcon = false,
}: SelectProps<Option, IsMulti, Group> & ExtraProps<Option, IsMulti>) => {
  const { show, close, visible } = useVisible();
  const { t } = useTranslation(['common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const SelectComponent = isCreatable ? StyledCreatableSelect : StyledSelect;
  const [innerInputValue, setInnerInputValue] = useState<string>();
  const [internalValue, setInternalValue] = useState<typeof value>(value);
  const [menuHeight, setMenuHeight] = useState<number>(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const showDesktopSearch = breakpointUpMd && isSearchable;
  const searchPortalRef = useRef<HTMLDivElement>(null);
  const [searchPortalReady, setSearchPortalReady] = useState(false);
  const onSearchPortalReady = useCallback(() => setSearchPortalReady(true), []);
  const { visualViewportHeight } = useGetVisualViewport();
  const [searchText, setSearchText] = useState('');
  const triggerTargetOverrideRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null);
  const onSearchRef = useRef(onSearch);
  const stackDepth = useOverlayStackDepth(visible ?? false);

  const handleClose = useCallback(() => {
    close();
    setInternalValue(value);
  }, [close, value]);

  const handleClear = useCallback(() => {
    setInternalValue(isMulti ? [] : null);
  }, [isMulti]);

  const handleApply = useCallback(
    (value: OnChangeValue<Option, IsMulti>) => {
      onChange(value);
      handleClose();
    },
    [handleClose, onChange],
  );

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const debouncedHandleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!onSearchRef.current) {
          return;
        }
        setIsSearching(true);
        await onSearchRef.current(value);
        setIsSearching(false);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  useEffect(() => {
    if (!visible) {
      setInternalValue(value);
    }
  }, [value, visible]);

  const showingSelectedCount = useMemo(
    () =>
      !innerInputValue &&
      isMulti &&
      !!internalValue &&
      'length' in internalValue &&
      internalValue.length > 0,
    [innerInputValue, internalValue, isMulti],
  );

  const showingSelectedCountClassName = showingSelectedCount
    ? ' is-showing-selected-count '
    : '';

  const isMultiClassname = isMulti ? ' is-multi-select ' : '';
  const isTagClassname = isTag ? ' is-tag-select ' : '';
  const isGroupSelectClassname =
    !!options && options.length > 0 && 'options' in options[0] ? 'is-grouped-select' : '';
  const isMultiGroupSelectClassname =
    isMulti && isGroupSelectClassname ? 'is-multi-grouped-select' : '';
  const effectiveOptions = useMemo(() => {
    if (!options?.length || !canLoadMore) {
      return options;
    }

    // To support loading state when we have grouped options
    const isGroupedOptions = !('value' in options[0]);
    if (isGroupedOptions) {
      return [...options, { label: null, options: [LOADING_ITEM] }];
    }

    return [...options, LOADING_ITEM];
  }, [canLoadMore, options]);

  const onInputSearch = useCallback(
    (value: string, actionMeta: InputActionMeta) => {
      // The on input search is called under many conditions, we only want to handle two cases:
      // The user is typing on the input: So we can grab the value make a request to get items matching that keyword
      // The user finished typing and selects an item from the list: This makes the input empty, so we want to refetch in order
      // to see the full list again
      if (
        actionMeta.action === 'input-change' ||
        (actionMeta.action === 'set-value' && actionMeta.prevInputValue)
      ) {
        setSearchText(value);
        onSearchTextChange?.(value);
        void debouncedHandleSearch(value);
      }
    },
    [debouncedHandleSearch, onSearchTextChange],
  );

  const filterOption = useMemo(() => {
    return canLoadMore || disableInternalSearch ? () => true : undefined;
  }, [canLoadMore, disableInternalSearch]);

  // react-select doesn't reliably reflect the controlled `value` becoming empty
  // when it changes as a result of clicking the already-selected option in
  // single-select mode (its own onChange fires with the same value while the
  // menu closes in the same update). Remounting on the has-value/no-value
  // transition forces it to pick up the cleared value. Scoped to single-select
  // only, since multi-select's own deselect path doesn't hit this issue and
  // remounting there would drop in-progress search/focus state on every
  // 0-to-1-selection transition.
  const singleSelectRemountKey = !isMulti && Boolean(value) ? 'has-value' : 'no-value';

  const mainSelect = useMemo(
    () => (
      <SelectContainer
        className={c({ [className]: true, 'has-custom-trigger': triggerOverride })}
      >
        {label && <Label>{label}</Label>}
        <SelectComponent
          key={singleSelectRemountKey}
          ref={selectRef as any}
          classNamePrefix="lgg-select"
          // @ts-expect-error lib complex types
          options={effectiveOptions}
          value={value}
          name={name}
          allowCreateWhileLoading={!isSearching}
          placeholder={placeholder}
          isLoading={isLoading}
          isClearable={isClearable}
          menuShouldBlockScroll
          menuPosition="fixed"
          className={`${isTagClassname} ${c({ 'input-error': hasError })}`}
          menuShouldScrollIntoView
          menuPlacement="auto"
          isSearchable={breakpointUpMd ? isSearchable : false}
          filterOption={filterOption}
          hideSelectedOptions={false}
          isDisabled={isDisabled}
          menuIsOpen={menuIsOpen ?? (breakpointUpMd ? visible : false)}
          onChange={onChange}
          onMenuClose={() => {
            debouncedHandleSearch.cancel();
            setSearchPortalReady(false);
            setSearchText('');
            onSearchTextChange?.('');
            onMenuClose?.();
            close();
            if (showDesktopSearch) {
              // When search is active the input is portaled into the menu. On menu
              // close the portal unmounts but react-select's isFocused stays true
              // because no blur event fires on the now-gone DOM node.
              selectRef.current?.blur();
            }
          }}
          isMulti={isMulti}
          onInputChange={onInputSearch}
          noOptionsMessage={() => <NoOptionsFound />}
          onMenuOpen={show}
          closeMenuOnSelect={!isMulti}
          controlShouldRenderValue
          // Override default behaviour because on Playwright this default mechanism is inconsistent
          // https://github.com/JedWatson/react-select/issues/4455#issuecomment-839930597
          blurInputOnSelect={false}
          components={{
            DropdownIndicator: (props) =>
              hideIndicators ? (
                <InvisibleElement />
              ) : (
                <DropdownIndicator {...props} forceCaret={breakpointUpMd && forceCaret} />
              ),
            IndicatorSeparator,
            LoadingIndicator,
            ClearIndicator: (props) =>
              hideIndicators ? <InvisibleElement /> : <ClearIndicator {...props} />,
            ...(!breakpointUpMd &&
              !forceCaret && { IndicatorsContainer: () => <InvisibleElement /> }),
            ...(triggerOverride && { IndicatorsContainer: () => <InvisibleElement /> }),
            Option: (props) => (
              <OptionComponent
                {...props}
                chipSize="medium"
                children={props.children}
                renderOption={renderOption}
              />
            ),
            MultiValueLabel,
            MultiValueRemove,
            Control,
            SingleValue: (props) => {
              const placeholderContent = (
                <div className="lgg-select__placeholder">
                  {renderPlaceholder ? renderPlaceholder() : placeholder}
                </div>
              );

              return renderValue ? (
                <components.SingleValue {...props}>
                  {props.hasValue ? renderValue(props.data) : placeholderContent}
                </components.SingleValue>
              ) : (
                <components.SingleValue {...props}>
                  {props.hasValue ? (
                    <SingleValueContainer>
                      {props.data.icon && typeof props.data.icon === 'string' ? (
                        <OptionIcon type={props.data.icon} />
                      ) : (
                        props.data.icon
                      )}
                      <span className="lgg-select__single-value-text">
                        {props.children}
                      </span>
                    </SingleValueContainer>
                  ) : (
                    placeholderContent
                  )}
                </components.SingleValue>
              );
            },
            MenuList: DesktopMenuList,
            Menu,
            MultiValue: (props) => (
              <MultiValueComponent {...props} maxToShow={multiValueMaxToShow} />
            ),
            // When the desktop search is on, the placeholder is rendered by
            // DesktopValueContainer instead (see the Placeholder override below).
            ...(renderPlaceholder && {
              Placeholder: (props: PlaceholderProps<Option, IsMulti, Group>) => (
                <components.Placeholder {...props}>
                  {renderPlaceholder()}
                </components.Placeholder>
              ),
            }),
            ...(showDesktopSearch && {
              Input: DesktopInput,
              ValueContainer: DesktopValueContainer,
              Placeholder: () => null,
            }),
          }}
        />
      </SelectContainer>
    ),
    [
      className,
      triggerOverride,
      label,
      SelectComponent,
      singleSelectRemountKey,
      effectiveOptions,
      value,
      name,
      isSearching,
      placeholder,
      isLoading,
      isClearable,
      isTagClassname,
      hasError,
      breakpointUpMd,
      isSearchable,
      filterOption,
      isDisabled,
      menuIsOpen,
      visible,
      onChange,
      isMulti,
      onInputSearch,
      show,
      forceCaret,
      renderPlaceholder,
      showDesktopSearch,
      debouncedHandleSearch,
      onSearchTextChange,
      onMenuClose,
      close,
      hideIndicators,
      renderOption,
      renderValue,
      multiValueMaxToShow,
    ],
  );

  const computeMenuHeight = useCallback(() => {
    const showingInput = isSearchable;
    const isKeyboardOpen = visualViewportHeight !== window.innerHeight;
    const menuHeight =
      visualViewportHeight - // Screen - Keyboard height
      5 - // Drawer mask
      70 - // Header
      (showingInput ? 59 : 0) - // Input
      (isKeyboardOpen ? 0 : 60) - // Footer
      (Capacitor.isNativePlatform() ? 80 : 0); // Bottom offset for capacitor

    setMenuHeight(menuHeight);
  }, [isSearchable, visualViewportHeight]);

  useEffect(() => {
    computeMenuHeight();
  }, [computeMenuHeight]);

  useEffect(() => {
    const handleResize = () => {
      computeMenuHeight();

      if (breakpointUpMd) {
        close();
      }
    };

    window.visualViewport!.addEventListener('resize', handleResize);

    return () => window.visualViewport!.removeEventListener('resize', handleResize);
  }, [breakpointUpMd, close, computeMenuHeight]);

  const height = useBottomDrawerHeight({ fullHeight: true });

  return (
    <SelectContext.Provider
      value={{
        onLoadMore,
        isSearching,
        setIsLoadingMore,
        isLoadingMore,
        showDesktopSearch,
        searchPortalRef,
        searchPortalReady,
        onSearchPortalReady,
        searchText,
        inputName: name,
        triggerOverride: breakpointUpMd
          ? triggerOverride?.(value as OnChangeValue<Option, IsMulti>)
          : undefined,
        triggerTargetOverrideRef,
        searchPlaceholder,
        renderValue: renderValue as
          | ((value: SelectOption) => React.ReactNode)
          | undefined,
        renderPlaceholder,
        showMultiValueIcon,
      }}
    >
      {breakpointUpMd ? (
        mainSelect
      ) : (
        <>
          <SelectWrapper className="select-wrapper">
            {triggerOverride
              ? triggerOverride(value as OnChangeValue<Option, IsMulti>)
              : mainSelect}
            <SelectInnerWrapper
              onClick={() => {
                if (!isDisabled) {
                  show();
                }
              }}
            />
          </SelectWrapper>
          <StyledDrawer
            data-lgg-id={`${name}-drawer`}
            zIndex={ModalsZIndex + (stackDepth ?? 0)}
            menuHeight={menuHeight}
            onClose={handleClose}
            visible={visible}
            destroyOnClose
            push={false}
            placement="bottom"
            closable={false}
            title={
              <DrawerHeader data-lgg-id={`${name}-drawer-header`}>
                <DrawerTitle>{mobileLabel ?? label}</DrawerTitle>
                <DrawerCloseIcon type="close" onClick={handleClose} />
              </DrawerHeader>
            }
            footer={
              <>
                <DrawerFooter>
                  {isClearable ? (
                    <ButtonV2 variant="default" size="regular" onClick={handleClear}>
                      {t('common:clear')}
                    </ButtonV2>
                  ) : null}
                  <ButtonV2
                    variant="primary"
                    size="regular"
                    onClick={() =>
                      handleApply(internalValue as OnChangeValue<Option, IsMulti>)
                    }
                  >
                    {t('common:set')}
                  </ButtonV2>
                </DrawerFooter>

                <SafeAreaBottom />
              </>
            }
            height={height}
          >
            <SelectComponent
              classNamePrefix="lgg-select"
              className={`lgg-drawer-select ${isGroupSelectClassname} ${showingSelectedCountClassName} ${isMultiClassname} ${isTagClassname} ${isMultiGroupSelectClassname}`}
              // @ts-expect-error lib complex types
              options={effectiveOptions}
              value={internalValue}
              isSearchable={isSearchable}
              isMulti={isMulti}
              onInputChange={(value, actionMeta) => {
                setInnerInputValue(value);

                onInputSearch(value, actionMeta);
              }}
              onChange={(newValue) => {
                setInternalValue(newValue);

                if (!isMulti) {
                  handleApply(newValue);
                }
              }}
              menuIsOpen
              filterOption={filterOption}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              openMenuOnFocus={false}
              components={{
                Control,
                Input,
                Option: (props) => (
                  <OptionComponent
                    {...props}
                    chipSize="large"
                    children={props.children}
                    renderOption={renderOption}
                  />
                ),
                ValueContainer,
                MenuList,
                IndicatorsContainer: () => <InvisibleElement />,
              }}
            />
          </StyledDrawer>
        </>
      )}
    </SelectContext.Provider>
  );
};
